import { ArrowRightOutlined, TeamOutlined } from '@ant-design/icons';
import { ConfigProvider, Progress, theme } from 'antd';
import clsx from 'clsx';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

import {
  DashboardMetrics,
  getOrganizationDashboard,
  getOrganizationDetails,
  getOrganizationMe,
  getOrganizationSpending,
  OrganizationProfile,
  SpendingMetrics,
} from '@/services/Organization';

export interface TopStatsProps {
  dashboardMetrics?: DashboardMetrics | null;
  spendingMetrics?: SpendingMetrics | null;
  profile?: OrganizationProfile | null;
  className?: string;
  hideIfNoData?: boolean;
}

const TopStats: React.FC<TopStatsProps> = ({
  dashboardMetrics,
  spendingMetrics,
  profile,
  className,
  hideIfNoData = false,
}) => {
  const router = useRouter();
  const isHomePage = router?.pathname === '/';
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(dashboardMetrics || null);
  const [spending, setSpending] = useState<SpendingMetrics | null>(spendingMetrics || null);
  const [orgProfile, setOrgProfile] = useState<OrganizationProfile | null>(profile || null);
  const [loading, setLoading] = useState(!dashboardMetrics && !spendingMetrics);
  const [hasData, setHasData] = useState(Boolean(dashboardMetrics || spendingMetrics || profile));

  useEffect(() => {
    if (dashboardMetrics) {
      setMetrics(dashboardMetrics);
      setHasData(true);
    }
  }, [dashboardMetrics]);

  useEffect(() => {
    if (spendingMetrics) {
      setSpending(spendingMetrics);
      setHasData(true);
    }
  }, [spendingMetrics]);

  useEffect(() => {
    if (profile) {
      setOrgProfile(profile);
      setHasData(true);
    }
  }, [profile]);

  useEffect(() => {
    let isMounted = true;

    const fetchAll = async () => {
      setLoading(true);
      try {
        const [dashRes, spendRes, detailsRes] = await Promise.allSettled([
          getOrganizationDashboard(),
          getOrganizationSpending(),
          getOrganizationDetails().catch(() => getOrganizationMe()),
        ]);

        if (!isMounted) return;

        let gotAnyData = false;
        if (dashRes.status === 'fulfilled' && dashRes.value) {
          setMetrics(dashRes.value);
          gotAnyData = true;
        }
        if (spendRes.status === 'fulfilled' && spendRes.value) {
          setSpending(spendRes.value);
          gotAnyData = true;
        }
        if (detailsRes.status === 'fulfilled' && detailsRes.value) {
          const orgData = (detailsRes.value as any)?.data || detailsRes.value;
          if (orgData) {
            setOrgProfile(orgData);
            gotAnyData = true;
          }
        }
        setHasData(gotAnyData);
      } catch {
        if (isMounted) setHasData(false);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (!dashboardMetrics || !spendingMetrics) {
      fetchAll();
    }

    return () => {
      isMounted = false;
    };
  }, []);

  const spentAmount = spending?.total_spent ?? metrics?.total_funds_spent ?? 0;
  const totalBudget =
    metrics?.total_funds_recharged ??
    metrics?.budget_allocated ??
    orgProfile?.wallet_balance ??
    (metrics?.total_funds_available !== undefined
      ? (metrics?.total_funds_available || 0) + spentAmount
      : 0);
  const percentUsed =
    metrics?.usage_percentage ??
    metrics?.budget_used_percentage ??
    (totalBudget > 0 ? Math.min(100, Math.round((spentAmount / totalBudget) * 100)) : 0);
  const employeeCount = metrics?.active_members_count ?? 0;
  const booksCompleted = metrics?.total_books_purchased ?? metrics?.books_completed_count ?? 0;
  const rawRank = metrics?.organization_rank ?? metrics?.company_rank;
  const companyRank = rawRank ? (typeof rawRank === 'number' ? `#${rawRank}` : rawRank) : '-';

  if (loading) {
    if (hideIfNoData) {
      return null;
    }
    return (
      <div className={clsx('grid grid-cols-1 lg:grid-cols-12 gap-4', className)}>
        <div className='lg:col-span-6 bg-[#0E0E0E] border border-[#1E1E1E] rounded-2xl p-5 h-36 animate-pulse flex flex-col justify-between'>
          <div className='h-4 bg-[#1C1C1C] rounded w-1/3' />
          <div className='h-8 bg-[#1C1C1C] rounded w-2/3' />
          <div className='h-2 bg-[#1C1C1C] rounded w-full' />
        </div>
        <div className='lg:col-span-3 bg-[#0E0E0E] border border-[#1E1E1E] rounded-2xl p-5 h-36 animate-pulse flex flex-col justify-between'>
          <div className='h-8 bg-[#1C1C1C] rounded w-1/2' />
          <div className='h-4 bg-[#1C1C1C] rounded w-3/4' />
        </div>
        <div className='lg:col-span-3 bg-[#0E0E0E] border border-[#1E1E1E] rounded-2xl p-5 h-36 animate-pulse flex flex-col justify-between'>
          <div className='h-8 bg-[#1C1C1C] rounded w-1/2' />
          <div className='h-4 bg-[#1C1C1C] rounded w-3/4' />
        </div>
      </div>
    );
  }

  if (hideIfNoData && !hasData) {
    return null;
  }

  return (
    <ConfigProvider
      theme={{
        algorithm: [theme.darkAlgorithm],
        token: {
          colorPrimary: '#FF5612',
        },
      }}
    >
      <div className={clsx('grid grid-cols-1 lg:grid-cols-12 gap-4', className)}>
        {/* Card 1: Balance Used & Employees (spans 6 cols on lg) */}
        <div className='lg:col-span-6 bg-[#0E0E0E] border border-[#1E1E1E] rounded-2xl p-5 flex flex-col justify-between hover:border-[#2E2E2E] transition-all shadow-sm'>
          {/* Header row */}
          <div className='flex items-center justify-between mb-3'>
            <span className='text-xs font-semibold text-grayZl-400'>
              Balance Used
            </span>
            <Link
              href={isHomePage ? '/corporate' : '/corporate/wallet'}
              className='text-xs font-bold text-[#FF5612] hover:text-[#E54D10] transition-colors hover:underline cursor-pointer'
            >
              {isHomePage ? 'History & Manage' : 'Add Balance'}
            </Link>
          </div>

          {/* Balance & Progress */}
          <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-4'>
            <div className='flex-1'>
              <div className='flex items-baseline justify-between mb-1.5'>
                <span className='text-lg font-bold text-white tracking-tight'>
                  ₹{(Number(spentAmount) || 0).toLocaleString('en-IN')}{' '}
                  <span className='text-grayZl-400 text-sm font-normal'>
                    / ₹{(Number(totalBudget) || 0).toLocaleString('en-IN')}
                  </span>
                </span>
                <span className='text-xs font-semibold text-grayZl-400'>
                  {percentUsed}%
                </span>
              </div>
              <Progress
                percent={percentUsed}
                showInfo={false}
                strokeColor='#FF5612'
                trailColor='#252525'
                size={['100%', 6]}
                className='!m-0'
              />
            </div>

            {/* Divider on sm */}
            <div className='hidden sm:block w-px h-10 bg-[#222222] mx-2' />

            {/* Employees metric */}
            <div className='flex items-center gap-3 min-w-[120px]'>
              <div className='w-9 h-9 rounded-full bg-[#181818] border border-[#2A2A2A] flex items-center justify-center text-grayZl-300'>
                <TeamOutlined className='text-sm text-grayZl-300' />
              </div>
              <div>
                <p className='text-[11px] text-grayZl-400 font-medium leading-none mb-1'>
                  Employees
                </p>
                <p className='text-xl font-extrabold text-white leading-none'>
                  {employeeCount}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Books Completed (spans 3 cols on lg) */}
        <div
          className='lg:col-span-3 bg-[#0E0E0E] border border-[#1E1E1E] rounded-2xl p-5 flex flex-col justify-between hover:border-[#2E2E2E] transition-all shadow-sm group cursor-pointer'
        >
          <div className='flex items-start justify-between'>
            <span className='text-4xl font-extrabold text-white tracking-tight'>
              {booksCompleted}
            </span>
            {/* <div className='w-7 h-7 rounded-full bg-[#161616] border border-[#262626] flex items-center justify-center text-grayZl-400 group-hover:text-white group-hover:border-neutral-500 transition-colors'>
              <ArrowRightOutlined className='text-xs -rotate-45' />
            </div> */}
          </div>
          <div className='mt-3'>
            <p className='text-xs text-grayZl-400 font-medium'>Books Purchased</p>
            <p className='text-xs font-bold text-[#FF5612] mt-0.5'>Learning active</p>
          </div>
        </div>

        {/* Card 3: Company Rank (spans 3 cols on lg) */}
        <Link
          href='/corporate/leaderboard'
          className='lg:col-span-3 bg-[#0E0E0E] border border-[#1E1E1E] rounded-2xl p-5 flex flex-col justify-between hover:border-[#2E2E2E] transition-all shadow-sm group cursor-pointer'
        >
          <div className='flex items-start justify-between'>
            <span className='text-4xl font-extrabold text-white tracking-tight'>
              {companyRank}
            </span>
            <div className='w-7 h-7 rounded-full bg-[#161616] border border-[#262626] flex items-center justify-center text-grayZl-400 group-hover:text-white group-hover:border-neutral-500 transition-colors'>
              <ArrowRightOutlined className='text-xs -rotate-45' />
            </div>
          </div>
          <div className='mt-3'>
            <p className='text-xs text-grayZl-400 font-medium'>Company Rank</p>
            <p className='text-xs font-semibold text-[#818CF8] mt-0.5'>Leaderboard Rank</p>
          </div>
        </Link>
      </div>
    </ConfigProvider>
  );
};

export default TopStats;
